/**
 * `buildTradeRowFromEvent` — event-as-row builder for the live trade tape.
 *
 * Context: `useGetTrades` is a `kind: multiple` list hook backing the
 * live-trade overlay on the `/markets` feed. Its realtime subject
 * (`morfi.market.{m}.user.*.trade.v0`, proto `morfi.v1.MarketTrade`)
 * fires on every trade. The old `invalidate: true` recipe dropped the
 * cached tape and refetched `GET /v1/trades` on EVERY event — a request
 * storm under sustained trade flow (devnet bots land ~1+/s across every
 * market × every mounted card). A live tape must instead PREPEND the
 * event payload as a new row — zero HTTP.
 *
 * This helper is the row builder behind the manifest `prepend:` recipe:
 * codegen emits an `applyEvent` that decodes the trade event, calls this
 * to construct the row, and unshifts it onto the cached `data` array.
 *
 * Honest partial row: the v0 trade event carries only what the indexer
 * puts on the wire — the unique `sequenceId` cursor, `ts`, `side`,
 * `actor` (trader wallet), the quote `amountD6`, `outcomeIdx`, and the
 * per-trade `prizePoolContributionD6`, and the execution-average price
 * (`executionPriceD9` → `priceAfter`). The post-trade spot price is a
 * distinct mark-price field used only for current outcome state. The
 * on-chain tx signature, the
 * fee split (platform / lp), the pre-trade price (`priceBefore`),
 * base-token amount, and the trader profile join (display name / handle /
 * avatar / userId) are server-computed and NOT on the realtime wire. Those
 * fields are filled with honest zeros / nulls — the freshest live rows are
 * an event projection; a full page reload re-snapshots the real rows from REST.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

/** `morfi.v1.Side` — `SIDE_SELL`. `SIDE_BUY` is 1; a trade event uses
 *  buy/sell only (back/unback ride other subjects). */
const SIDE_SELL = 2;

/** Hex-encode a byte array (no `0x` prefix). */
function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

/** Coerce an unknown D6 value (bigint | number | base-10 string |
 *  nullish) to a base-10 string; nullish / malformed → `'0'`. */
function d6String(v: unknown): string {
  if (typeof v === 'bigint') return v.toString();
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return v.trim();
  return '0';
}

function stringArg(args: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = args?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

function csvContains(raw: string, expected: string): boolean {
  return raw.split(',').some((value) => value.trim() === expected);
}

function listArgContains(
  args: Record<string, unknown> | undefined,
  key: string,
  expected: string,
): boolean {
  const value = args?.[key];
  if (Array.isArray(value)) {
    return (
      value.length === 0 ||
      value.some((candidate) => typeof candidate === 'string' && candidate.trim() === expected)
    );
  }
  const encoded = stringArg(args, key);
  return encoded === undefined || csvContains(encoded, expected);
}

interface MarketMintRoute {
  readonly market: string;
  readonly outcomeIndex: number;
}

function marketMintRoute(
  args: Record<string, unknown> | undefined,
  mint: string,
): MarketMintRoute | undefined {
  const encoded = stringArg(args, 'marketMintRoutes');
  if (encoded === undefined) return undefined;
  for (const entry of encoded.split(',')) {
    const [candidateMint, market, rawOutcomeIndex] = entry.split(':');
    const outcomeIndex = Number(rawOutcomeIndex);
    if (
      candidateMint === mint &&
      market !== undefined &&
      market.length > 0 &&
      Number.isInteger(outcomeIndex) &&
      outcomeIndex >= 0
    ) {
      return { market, outcomeIndex };
    }
  }
  return undefined;
}

interface TradeFilterValues {
  readonly signature: string;
  readonly market: string;
  readonly actor: string;
  readonly outcomeIndex: number;
  readonly type: 'buy' | 'sell' | 'swap';
  readonly timestamp: string;
}

/** Apply the same filters exposed by GET /v1/trades before prepending a wire row. */
function matchesTradeFilters(
  args: Record<string, unknown> | undefined,
  values: TradeFilterValues,
): boolean {
  const market = stringArg(args, 'market');
  if (market !== undefined && market !== values.market) return false;

  if (!listArgContains(args, 'markets', values.market)) return false;

  const user = stringArg(args, 'user');
  if (user !== undefined && user !== values.actor) return false;

  if (!listArgContains(args, 'users', values.actor)) return false;

  const outcomeIndex = args?.outcomeIndex;
  if (outcomeIndex !== undefined && Number(outcomeIndex) !== values.outcomeIndex) return false;

  const type = stringArg(args, 'type');
  if (type !== undefined && type !== values.type) return false;

  const timestampMs = Date.parse(values.timestamp);
  const from = stringArg(args, 'from');
  if (from !== undefined) {
    const fromMs = Date.parse(from);
    if (!Number.isFinite(fromMs) || timestampMs < fromMs) return false;
  }
  const to = stringArg(args, 'to');
  if (to !== undefined) {
    const toMs = Date.parse(to);
    if (!Number.isFinite(toMs) || timestampMs > toMs) return false;
  }

  if (!listArgContains(args, 'signatures', values.signature)) return false;

  return true;
}

/**
 * Row shape produced by `buildTradeRowFromEvent`. Structurally matches
 * the codegen-generated `GetTradesRow` (a list-row of `useGetTrades`).
 * Declared by hand here so the helper does not import generated code
 * (codegen output imports helpers — the reverse would be a cycle).
 */
export interface BuiltTradeRow {
  readonly eventId: string;
  readonly signature: string;
  readonly market: string;
  readonly outcomeIndex: number;
  readonly type: 'buy' | 'sell' | 'swap';
  readonly side: 'buy' | 'sell';
  readonly liquidityModel: 'bonding_curve' | 'cp_swap' | null;
  readonly usdcAmount: string;
  readonly tokensAmount: string;
  readonly totalCost: string;
  readonly platformFee: string;
  readonly lpFee: string;
  readonly prizeFee: string;
  readonly priceBefore: string;
  readonly priceAfter: string;
  readonly timestamp: string;
  readonly thesis: string | null;
  readonly trader: {
    readonly userId: string | null;
    readonly walletAddress: string;
    readonly displayName: string | null;
    readonly handle: string | null;
    readonly avatarUrl: string | null;
    readonly avatarNsfw: boolean;
    readonly avatarPreferences: { readonly rasterise: boolean };
    readonly connectedProviders: readonly [];
  };
}

/**
 * Scope-skip sentinel — an empty `eventId` tells the codegen-emitted
 * `applyEvent` to bail (no prepend). Returned when a trade event isn't for
 * the cache slice the consuming `useGetTrades({ market })` hook asked for.
 * Mirrors `EMPTY_BUILT_GIFT_ROW` (gift-row.ts) / `skip()` (position-row.ts).
 */
const EMPTY_TRADE_ROW: BuiltTradeRow = {
  eventId: '',
  signature: '',
  market: '',
  outcomeIndex: 0,
  type: 'buy',
  side: 'buy',
  liquidityModel: null,
  usdcAmount: '0',
  tokensAmount: '0',
  totalCost: '0',
  platformFee: '0',
  lpFee: '0',
  prizeFee: '0',
  priceBefore: '0',
  priceAfter: '0',
  timestamp: '',
  thesis: null,
  trader: {
    userId: null,
    walletAddress: '',
    displayName: null,
    handle: null,
    avatarUrl: null,
    avatarNsfw: false,
    avatarPreferences: { rasterise: true },
    connectedProviders: [],
  },
};

/**
 * Build a `useGetTrades` list row from a decoded `MarketTrade`
 * trade event.
 *
 * @param raw     the decoded protobuf message (`fromBinary` output),
 *                exposed by codegen as `Record<string, unknown>`.
 * @param subject the concrete NATS subject the event arrived on
 *                (`morfi.market.<MARKET>.user.<actor>.trade.v0`) — the
 *                market pubkey is segment index 2.
 * @param args    the consuming hook's args — used to scope-skip events
 *                outside its singular or multi-market filter.
 */
export function buildTradeRowFromEvent(
  raw: Record<string, unknown>,
  subject: string,
  args?: Record<string, unknown>,
): BuiltTradeRow {
  const seq = raw.sequenceId;
  const actor = typeof raw.actor === 'string' ? raw.actor : '';
  const eventId = seq instanceof Uint8Array && seq.length === 15 ? bytesToHex(seq) : '';
  if (eventId === '') return EMPTY_TRADE_ROW;
  const signature = typeof raw.signature === 'string' ? raw.signature : '';
  // `morfi` . `market` . <MARKET> . `user` . <actor> . `trade` . `v0`
  const market = subject.split('.')[2] ?? '';
  // Scope-skip remains the cache-safety boundary even though the wire subject
  // is narrowed to the singular/list market args. Bail (empty eventId →
  // codegen's prepend bail-out) if a mismatched event is ever dispatched.
  const outcomeIndex = Number(raw.outcomeIdx ?? 0) || 0;
  const side: 'buy' | 'sell' = Number(raw.side) === SIDE_SELL ? 'sell' : 'buy';
  const usdcAmount = d6String(raw.amountD6);
  // `ts` is chain `Clock` time (epoch seconds). Render an ISO string for
  // the row's `timestamp`. A value already in ms (> 1e12) is passed
  // through; a missing / malformed ts falls back to wall-clock now.
  const tsNum = Number(raw.ts ?? 0);
  const ms = tsNum > 1e12 ? tsNum : tsNum * 1000;
  const timestamp = new Date(Number.isFinite(ms) && ms > 0 ? ms : Date.now()).toISOString();
  if (
    !matchesTradeFilters(args, {
      market,
      signature,
      actor,
      outcomeIndex,
      type: side,
      timestamp,
    })
  ) {
    return EMPTY_TRADE_ROW;
  }
  return {
    eventId,
    signature,
    market,
    outcomeIndex,
    type: side,
    side,
    liquidityModel: 'bonding_curve',
    usdcAmount,
    // Not on the realtime wire — server-computed. Honest zeros until a
    // full page reload re-snapshots the real row from REST.
    tokensAmount: '0',
    totalCost: usdcAmount,
    platformFee: '0',
    lpFee: '0',
    prizeFee: d6String(raw.prizePoolContributionD6),
    // `priceBefore` is not on the wire. `priceAfter` preserves the REST/CAGG
    // execution-average meaning. Old payloads omit `executionPriceD9`, so this
    // stays zero and the chart drops the point until reconcile fetches REST;
    // it must never substitute the distinct post-trade spot mark.
    priceBefore: '0',
    priceAfter: d6String(raw.executionPriceD9),
    timestamp,
    thesis: null,
    // `actor` is the trader wallet — the only identity on the wire. The
    // profile join (display name / handle / avatar / userId) is
    // server-side and absent from the realtime event.
    trader: {
      userId: null,
      walletAddress: actor,
      displayName: null,
      handle: null,
      avatarUrl: null,
      avatarNsfw: false,
      avatarPreferences: { rasterise: true },
      connectedProviders: [],
    },
  };
}

/**
 * Build a `useGetTrades` list row from a decoded `AmmUserActivity` swap
 * event (`morfi.amm.<MINT>.user.<actor>.activity.v0`).
 *
 * Differs from the bonding-curve builder in two ways: the market pubkey is
 * NOT in the subject (segment 2 is the MINT) — it rides the payload as
 * `market` — and the AMM event carries the post-swap spot price
 * (`postOutcomePriceD9`, D9) for current-state marking and the distinct exact
 * execution average (`executionPriceD9`) which fills `priceAfter`. Older
 * payloads omit execution provenance, so the row uses zero and reconciliation
 * replaces it from REST rather than substituting post-swap spot. Graduated
 * tokens have no prize-pool fee, so `prizeFee` is `'0'`. Quote notional is
 * `amountD6`.
 */
export function buildTradeRowFromAmmEvent(
  raw: Record<string, unknown>,
  _subject: string,
  args?: Record<string, unknown>,
): BuiltTradeRow {
  const seq = raw.sequenceId;
  const actor = typeof raw.actor === 'string' ? raw.actor : '';
  const eventId = seq instanceof Uint8Array && seq.length === 15 ? bytesToHex(seq) : '';
  if (eventId === '') return EMPTY_TRADE_ROW;
  const signature = typeof raw.signature === 'string' ? raw.signature : '';
  // Pre-persist AMM events leave `market` at the proto default. A detail tape
  // supplies one `args.market`; the aggregate feed supplies a compact
  // mint→market/outcome route. Both remain wire-bounded by `args.mints`.
  const mint = _subject.split('.')[2] ?? '';
  const route = marketMintRoute(args, mint);
  const payloadMarket = typeof raw.market === 'string' ? raw.market.trim() : '';
  const requestedMarket = stringArg(args, 'market');
  const market = payloadMarket || requestedMarket || route?.market || '';
  // AMM pre-persist events leave `outcomeIdx` at the protobuf default. A
  // winner query is already bound to one mint subject, so its explicit REST
  // outcome filter is the authoritative index for the realtime row too.
  const requestedOutcomeIndex = Number(args?.outcomeIndex);
  const outcomeIndex =
    Number.isInteger(requestedOutcomeIndex) && requestedOutcomeIndex >= 0
      ? requestedOutcomeIndex
      : (route?.outcomeIndex ?? (Number(raw.outcomeIdx ?? 0) || 0));
  const side: 'buy' | 'sell' = Number(raw.side) === SIDE_SELL ? 'sell' : 'buy';
  const usdcAmount = d6String(raw.amountD6);
  const priceAfter = d6String(raw.executionPriceD9);
  const tsNum = Number(raw.ts ?? 0);
  const ms = tsNum > 1e12 ? tsNum : tsNum * 1000;
  const timestamp = new Date(Number.isFinite(ms) && ms > 0 ? ms : Date.now()).toISOString();
  if (
    !matchesTradeFilters(args, {
      market,
      signature,
      actor,
      outcomeIndex,
      type: 'swap',
      timestamp,
    })
  ) {
    return EMPTY_TRADE_ROW;
  }
  return {
    eventId,
    signature,
    market,
    outcomeIndex,
    type: 'swap',
    side,
    liquidityModel: 'cp_swap',
    usdcAmount,
    // Outcome-token amount is not on the AMM wire — honest zero until a full
    // page reload re-snapshots the real row from REST.
    tokensAmount: '0',
    totalCost: usdcAmount,
    platformFee: '0',
    lpFee: '0',
    prizeFee: '0',
    priceBefore: '0',
    priceAfter,
    timestamp,
    thesis: null,
    trader: {
      userId: null,
      walletAddress: actor,
      displayName: null,
      handle: null,
      avatarUrl: null,
      avatarNsfw: false,
      avatarPreferences: { rasterise: true },
      connectedProviders: [],
    },
  };
}
